import { Select } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form/lib/use-form';

import React, { useEffect, useState } from 'react';
import {
  findValueInData,
  getMerchantFilterData,
} from '../../helpers/filters.helpers';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { EntityFilterData, EntityIdFilterData } from '../../type/filter.types';

interface Props {
  filterSelectData: UseFormReturnType<EntityFilterData>;
  filterValues: UseFormReturnType<EntityIdFilterData>;
  isResellerDependent?: boolean;
  error?: React.ReactNode;
}

function SelectMerchantFilter(props: Props) {
  const { filterSelectData, filterValues, isResellerDependent, error } = props;
  const [isLoading, setIsLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const noData = 'No data';
  const loadingData = 'Loading...';
  const { auth } = useAuth();

  const loadMerchants = async (partnerId?: string, resellerId?: string) => {
    setIsLoading(true);

    const merchants = await getMerchantFilterData(
      axiosPrivate,
      partnerId,
      resellerId
    );

    if (filterValues.values.merchant && merchants) {
      const found = findValueInData(merchants, filterValues.values.merchant);
      if (!found) {
        filterValues.setFieldValue('merchant', '');
      }
    }

    filterSelectData.setFieldValue('merchantsData', merchants);

    setIsLoading(false);
  };

  useEffect(() => {
    if (!isResellerDependent) {
      loadMerchants();
    }
  }, []);

  useDidMountEffect(() => {
    if (!isResellerDependent) {
      return;
    }

    if (
      filterValues.values.reseller &&
      filterValues.values.reseller !== noData
    ) {
      loadMerchants(filterValues.values.partner, filterValues.values.reseller);
    } else {
      filterValues.setFieldValue('merchant', '');
      filterSelectData.setFieldValue('merchantsData', []);
    }
  }, [filterValues.values.reseller]);

  const getData = () => {
    let data = isLoading
      ? [loadingData]
      : filterSelectData.values.merchantsData?.length === 0 ||
        filterSelectData.values.merchantsData === undefined
      ? [noData]
      : filterSelectData.values.merchantsData;

    if (isResellerDependent) {
      data = isLoading
        ? [loadingData]
        : (filterSelectData.values.merchantsData?.length === 0 &&
            filterValues.values.reseller) ||
          filterSelectData.values.merchantsData === undefined
        ? [noData]
        : filterSelectData.values.merchantsData;
    }

    return data;
  };

  const getValue = () => {
    let value = isLoading
      ? loadingData
      : filterSelectData.values.merchantsData?.length === 0 ||
        filterSelectData.values.merchantsData === undefined
      ? noData
      : filterValues.values.merchant;

    if (isResellerDependent) {
      value = isLoading
        ? loadingData
        : (filterSelectData.values.merchantsData?.length === 0 &&
            filterValues.values.reseller) ||
          filterSelectData.values.merchantsData === undefined
        ? noData
        : filterValues.values.merchant;
    }

    return value;
  };

  const getDisabled = () => {
    let disabled =
      isLoading ||
      filterSelectData.values.merchantsData?.length === 0 ||
      filterSelectData.values.merchantsData === undefined;

    if (isResellerDependent) {
      disabled =
        isLoading ||
        filterSelectData.values.merchantsData?.length === 0 ||
        filterSelectData.values.merchantsData === undefined ||
        !filterValues.values.reseller;
    }
    return disabled;
  };

  return (
    <>
      <Select
        label='Select Merchant'
        data={getData()}
        value={getValue()}
        disabled={getDisabled()}
        onChange={(value: string) =>
          filterValues.setFieldValue('merchant', value)
        }
        clearable={!getDisabled()}
        searchable
        error={error}
      />
    </>
  );
}

export default SelectMerchantFilter;
